import { defineStore } from 'pinia';
import { listForms, getFormDefinition } from '@vft/api/definitionApi.js';
import { looksLikeDatePattern, datePatternToRegex } from '@vft/core/definition/xlsxDefinitionParser.js';

// El RPC `formdefinition` exposa el format com a { lengthMin, lengthMax, regExp },
// pero per a CAMPS DE DATA el `regExp` porta el PATRO literal (p. ex. 'dd/MM/yyyy'),
// no una regex. Sense `datePattern` el programa `dateFormat` (pas 6) no dispara i la
// validacio de format rebutjaria qualsevol data. Aqui derivem datePattern + regex
// real, igual que fa el parser XLSX, perque les dues vies (XLSX i RPC) tinguin el
// mateix shape de camp.
function normalizeFieldFormats(fields) {
  if (!Array.isArray(fields)) return [];
  return fields.map((f) => {
    const fmt = f && f.validation && f.validation.format ? f.validation.format : null;
    if (!fmt) return f;
    // Dominis 'close-*' NO inline (close-api, close-external, close-conditional): el backend
    // posa una REFERÈNCIA del domini al slot regExp del format — un endpoint
    // ('/survey/stateone/domain') o una font externa ('DomAppCountryNameOrdered:domLang=nn_NN').
    // NO és un patró de validació: si es valida com a regex rebutja TOTS els valors. El
    // descartem — les claus vàlides les serveix l'endpoint/font externa. El domini 'close' pla
    // (inline) NO entra aquí i conserva el seu regExp si en té.
    const domainType = f.validation?.domainType;
    const isRefDomain = typeof domainType === 'string' && domainType.toLowerCase().startsWith('close-');
    if (isRefDomain && fmt.regExp) {
      return { ...f, validation: { ...f.validation, format: { ...fmt, regExp: null } } };
    }
    if (fmt.datePattern || !fmt.regExp) return f;
    const rx = String(fmt.regExp);
    if (!looksLikeDatePattern(rx)) return f;
    return {
      ...f,
      validation: {
        ...f.validation,
        format: { ...fmt, datePattern: rx, regExp: datePatternToRegex(rx) },
      },
    };
  });
}

export const useDefinitionStore = defineStore('definition', {
  state: () => ({
    forms: [],
    formsLoaded: false,
    selectedFormId: null,
    selectedLang: null,
    definition: null,
    source: null,
    loading: false,
    error: null,
    apiAvailable: true,
  }),
  getters: {
    hasDefinition: (s) => !!s.definition,
    fields: (s) => {
      if (!s.definition) return [];
      const arr = s.definition.requestDefinition || s.definition.fields || s.definition;
      return Array.isArray(arr) ? arr : [];
    },
  },
  actions: {
    async loadForms() {
      this.loading = true;
      this.error = null;
      try {
        const data = await listForms();
        this.forms = Array.isArray(data) ? data : data?.items || [];
        this.formsLoaded = true;
        this.apiAvailable = true;
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          this.error = { code: status, statusText: err.response?.statusText || '', kind: 'auth' };
        } else {
          this.error = err?.message || 'No s\'han pogut carregar els formularis';
        }
        this.apiAvailable = false;
      } finally {
        this.loading = false;
      }
    },
    // Marca un formulari com a triat SENSE carregar-ne la definició (encara no s'ha
    // triat idioma de càrrega). La definició es carrega a selectForm() quan l'usuari
    // tria idioma → fins llavors hasDefinition és false i no es pot avançar.
    pickForm(formId) {
      this.selectedFormId = formId;
      this.selectedLang = null;
      this.definition = null;
      this.error = null;
    },
    async selectForm(formId, lang = null) {
      this.selectedFormId = formId;
      this.selectedLang = lang;
      this.loading = true;
      this.error = null;
      try {
        const fields = normalizeFieldFormats(await getFormDefinition(formId, lang));
        this.definition = fields.length ? { fields } : null;
        if (!fields.length) this.error = 'El formulari no té camps actius';
        this.source = 'api';
      } catch (err) {
        this.error = err?.message || 'No s\'ha pogut carregar la definició';
        this.definition = null;
      } finally {
        this.loading = false;
      }
    },
    reset() {
      this.selectedFormId = null;
      this.definition = null;
      this.source = null;
      this.error = null;
    },
  },
});
